/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Analytics › Tech (contract section 5): the users and sessions by surface, app version, OS version and device class, from
 * GET /v1/admin/usage/tech with the lens; each split with its share of users.
 */
import { MonitorSmartphone } from 'lucide-react';
import { api } from '../../lib/api';
import { normalizeTech, splitTotal, surfaceLabel, type SplitRow } from '../../lib/usage';
import { EmptyState, Grid, Panel, ShareBar, Stack, Table, Tag, type Column } from '../../ui';
import { count } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { UsageError, type SectionProps } from './parts';

export function UsageTech({ lens, query, lensKey }: SectionProps) {
  const { load, reload, reloading } = useLoad(() => api.usageTech(query).then(normalizeTech), `tech|${lensKey}`);
  const data = load.state === 'ready' ? load.data : null;
  const loading = load.state === 'loading';
  if (load.state === 'failed') return <Panel title="Technology"><UsageError title="Technology could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /></Panel>;

  const split = (title: string, description: string, rows: SplitRow[], label: (key: string) => string = (key) => key) => {
    const total = splitTotal(rows);
    const columns: Array<Column<SplitRow>> = [
      { key: 'key', header: title, cell: (row) => <Tag mono={false}>{label(row.key)}</Tag> },
      { key: 'users', header: 'Users', numeric: true, cell: (row) => count(row.users) },
      { key: 'share', header: 'Share', width: '160px', cell: (row) => <ShareBar value={total > 0 ? row.users / total : 0} label={`${label(row.key)}: share of users`} /> },
      { key: 'sessions', header: 'Sessions', numeric: true, cell: (row) => count(row.sessions) },
    ];
    return (
      <Panel flush title={title} description={description}>
        <Table label={title} columns={columns} rows={rows} rowKey={(row) => row.key} loading={loading} skeletonRows={3} stickyHeader={false} empty={<EmptyState compact icon={MonitorSmartphone} title={`No ${title.toLowerCase()} yet`}>Splits appear here as people use Gyro; the lens above may be leaving people out.</EmptyState>} />
      </Panel>
    );
  };

  return (
    <Stack>
      <Grid cols={2}>
        {split('Surfaces', `Users and sessions by surface, last ${lens.window} days`, data?.surfaces ?? [], surfaceLabel)}
        {split('Device classes', 'Desktop, phone or tablet', data?.deviceClasses ?? [])}
      </Grid>
      <Grid cols={2}>
        {split('App versions', 'The widget, agent or add-in version each batch reported', data?.appVersions ?? [])}
        {split('OS versions', 'Windows, macOS or the browser, as reported without a full user agent', data?.osVersions ?? [])}
      </Grid>
    </Stack>
  );
}
